import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft,
  ArrowRight,
  Briefcase,
  MapPin,
  Clock,
  Users,
  Zap,
  Heart,
  GraduationCap,
  Rocket,
  Shield,
  Globe,
  Coffee,
  Target,
  ChevronRight,
  Search,
  Building,
  Linkedin,
  Twitter,
  Mail,
  ExternalLink,
  X
} from 'lucide-react';
import { companyInfo, socialLinks, contactInfo } from '../data/mock';

const CareersPage = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleApply = (position) => {
    setSelectedPosition(position);
    setShowModal(true);
  };

  const generateMailtoLink = () => {
    const position = selectedPosition?.title || 'General Application';
    const subject = encodeURIComponent(`Job Application: ${position} - Gegadyne Energy`);
    const body = encodeURIComponent(
`Dear Hiring Team,

I am writing to express my interest in the ${position} position at Gegadyne Energy.

---
Please include the following in your application:

Full Name:
Phone Number:
Years of Experience:
LinkedIn Profile:

Cover Letter / Why I'm a great fit:
[Your message here]

---
I have attached my resume for your review.

Thank you for considering my application.

Best regards`
    );
    return `mailto:${contactInfo.email}?subject=${subject}&body=${body}`;
  };

  const departments = [
    { id: 'all', name: 'All Departments' },
    { id: 'rd', name: 'Research & Development' },
    { id: 'engineering', name: 'Engineering' },
    { id: 'manufacturing', name: 'Manufacturing' },
    { id: 'product', name: 'Product' },
    { id: 'sales', name: 'Sales & Marketing' },
    { id: 'operations', name: 'Operations' },
  ];

  const openPositions = [
    {
      id: 1,
      title: 'Senior Battery Research Scientist',
      department: 'rd',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '5-8 years',
      description: 'Lead research initiatives in electrochemistry and materials science for next-generation battery development.',
      skills: ['Electrochemistry', 'Materials Science', 'Battery Testing', 'Data Analysis'],
      featured: true
    },
    {
      id: 2,
      title: 'Electrochemistry Engineer',
      department: 'engineering',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '3-5 years',
      description: 'Design and optimize electrochemical systems for Multi-Ion battery technology.',
      skills: ['Electrochemistry', 'Cell Design', 'Testing & Validation', 'Python'],
      featured: true
    },
    {
      id: 3,
      title: 'Product Manager - Energy Storage',
      department: 'product',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '4-7 years',
      description: 'Define product roadmap and strategy for energy storage solutions targeting industrial applications.',
      skills: ['Product Strategy', 'Market Analysis', 'Technical Background', 'B2B Sales'],
      featured: false
    },
    {
      id: 4,
      title: 'Manufacturing Engineer',
      department: 'manufacturing',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '3-6 years',
      description: 'Optimize manufacturing processes for battery cell and pack production at scale.',
      skills: ['Process Engineering', 'Lean Manufacturing', 'Quality Control', 'Automation'],
      featured: false
    },
    {
      id: 5,
      title: 'Battery Test Engineer',
      department: 'engineering',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '2-4 years',
      description: 'Design and execute comprehensive testing protocols for battery cells and packs.',
      skills: ['Battery Testing', 'Data Analysis', 'Test Automation', 'Safety Standards'],
      featured: false
    },
    {
      id: 6,
      title: 'Sales Engineer - Industrial',
      department: 'sales',
      location: 'Mumbai / Remote',
      type: 'Full-time',
      experience: '3-5 years',
      description: 'Drive business development for traction and energy storage battery solutions.',
      skills: ['Technical Sales', 'B2B', 'Industrial Equipment', 'Relationship Building'],
      featured: false
    },
    {
      id: 7,
      title: 'Materials Scientist',
      department: 'rd',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '2-5 years',
      description: 'Research and develop novel electrode materials for improved battery performance.',
      skills: ['Materials Characterization', 'Synthesis', 'XRD/SEM', 'Publications'],
      featured: false
    },
    {
      id: 8,
      title: 'Supply Chain Manager',
      department: 'operations',
      location: 'Mumbai',
      type: 'Full-time',
      experience: '5-8 years',
      description: 'Manage end-to-end supply chain operations for battery manufacturing.',
      skills: ['Supply Chain', 'Vendor Management', 'Inventory', 'Cost Optimization'],
      featured: false
    },
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Health & Wellness',
      description: 'Comprehensive health insurance for you and your family, mental health support, and wellness programs.',
      iconColor: 'text-rose-400',
      bgColor: 'bg-rose-500/10'
    },
    {
      icon: GraduationCap,
      title: 'Learning & Development',
      description: 'Continuous learning opportunities, conference sponsorships, and access to online courses.',
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10'
    },
    {
      icon: Rocket,
      title: 'Growth Opportunities',
      description: 'Fast-track career growth in a rapidly scaling deep-tech company with global ambitions.',
      iconColor: 'text-orange-400',
      bgColor: 'bg-orange-500/10'
    },
    {
      icon: Coffee,
      title: 'Work-Life Balance',
      description: 'Flexible working hours, hybrid work options, and generous paid time off.',
      iconColor: 'text-amber-400',
      bgColor: 'bg-amber-500/10'
    },
    {
      icon: Users,
      title: 'Collaborative Culture',
      description: 'Work alongside world-class researchers, engineers, and industry veterans.',
      iconColor: 'text-violet-400',
      bgColor: 'bg-violet-500/10'
    },
    {
      icon: Zap,
      title: 'Impactful Work',
      description: 'Contribute to technology that will shape the future of sustainable energy storage.',
      iconColor: 'text-emerald-400',
      bgColor: 'bg-emerald-500/10'
    },
  ];

  const values = [
    {
      icon: Target,
      title: 'Innovation First',
      description: 'We push the boundaries of what\'s possible in battery technology.'
    },
    {
      icon: Shield,
      title: 'Safety Always',
      description: 'Safety is not a feature—it\'s the foundation of everything we build.'
    },
    {
      icon: Users,
      title: 'Customer Focus',
      description: 'We engineer solutions for real-world problems, not laboratory benchmarks.'
    },
    {
      icon: Globe,
      title: 'Sustainable Impact',
      description: 'We\'re building technology that enables a cleaner, more sustainable future.'
    },
  ];

  const filteredPositions = openPositions.filter(position => {
    const matchesDepartment = selectedDepartment === 'all' || position.department === selectedDepartment;
    const matchesSearch = position.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          position.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesDepartment && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gray-950" data-testid="careers-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-950/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-10 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="back-to-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section with Team Background */}
        <section className="relative py-20 lg:py-32 overflow-hidden min-h-[600px] flex items-center">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="/images/careers-team.jpeg" 
              alt="Gegadyne Energy Team"
              className="w-full h-full object-cover object-center"
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-gray-950/70 via-gray-950/60 to-gray-950/85" />
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-6 backdrop-blur-sm">
                <Briefcase className="w-4 h-4 text-emerald-400" />
                <span className="text-emerald-400 text-sm font-medium">Careers at Gegadyne</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                Build the Future of 
                <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent"> Energy Storage</span>
              </h1>
              
              <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto drop-shadow-md">
                Join a team of passionate innovators working on breakthrough battery technology 
                that will power the world's transition to sustainable energy.
              </p>

              <div className="flex flex-wrap gap-4 justify-center">
                <a 
                  href="#openings" 
                  className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 px-8 rounded-xl transition-all shadow-lg"
                  data-testid="view-openings-btn"
                >
                  View Open Positions
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a 
                  href="#culture" 
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-4 px-8 rounded-xl border border-white/20 transition-all backdrop-blur-sm"
                >
                  Our Culture
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-900/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              {[
                { value: '2017', label: 'Founded' },
                { value: 'Mumbai', label: 'Headquarters' },
                { value: 'Multiple', label: 'Patents Filed & Granted' },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-emerald-400 mb-2">{stat.value}</p>
                  <p className="text-gray-400">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Join Us Section */}
        <section id="culture" className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Why Join Gegadyne?
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                We're not just building batteries—we're building the future of energy storage. 
                Here's why talented people choose to work with us.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div 
                    key={index}
                    className="bg-gray-900/50 rounded-2xl p-8 border border-white/5 hover:border-white/20 transition-all"
                  >
                    <div className={`w-14 h-14 rounded-xl ${benefit.bgColor} flex items-center justify-center mb-6`}>
                      <Icon className={`w-7 h-7 ${benefit.iconColor}`} />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                    <p className="text-gray-400">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Our Values Section */}
        <section className="py-20 bg-gray-900/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our Values
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                These principles guide everything we do—from how we develop technology to how we work together.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div 
                    key={index}
                    className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-2xl p-6 border border-emerald-500/20 text-center"
                  >
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-6 h-6 text-emerald-400" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                    <p className="text-gray-400 text-sm">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Life at Gegadyne */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Life at Gegadyne
                </h2>
                <p className="text-gray-400 text-lg mb-6">
                  We're a team of curious minds, groundbreakers, and innovation drivers united in 
                  our mission to revolutionize energy storage. Our culture is built on collaboration, 
                  continuous learning, and the freedom to experiment.
                </p>
                <ul className="space-y-4">
                  {[
                    'Work on cutting-edge technology with real-world impact',
                    'Collaborate with world-class researchers and engineers',
                    'Fast-paced environment with rapid learning opportunities',
                    'Be part of India\'s clean energy revolution',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-2xl p-8 border border-emerald-500/30">
                  <Building className="w-10 h-10 text-emerald-400 mb-4" />
                  <h3 className="text-white font-semibold mb-2">Modern Facility</h3>
                  <p className="text-gray-400 text-sm">State-of-the-art R&D and manufacturing facility in Mumbai</p>
                </div>
                <div className="bg-gray-800/50 rounded-2xl p-8 border border-white/5">
                  <Users className="w-10 h-10 text-cyan-400 mb-4" />
                  <h3 className="text-white font-semibold mb-2">Diverse Team</h3>
                  <p className="text-gray-400 text-sm">Engineers, scientists, and innovators from top institutions</p>
                </div>
                <div className="bg-gray-800/50 rounded-2xl p-8 border border-white/5">
                  <Rocket className="w-10 h-10 text-purple-400 mb-4" />
                  <h3 className="text-white font-semibold mb-2">Fast Growth</h3>
                  <p className="text-gray-400 text-sm">Rapid scaling with backed by leading investors</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-500/20 to-purple-500/20 rounded-2xl p-8 border border-cyan-500/30">
                  <Globe className="w-10 h-10 text-cyan-400 mb-4" />
                  <h3 className="text-white font-semibold mb-2">Global Impact</h3>
                  <p className="text-gray-400 text-sm">Technology that will power industries worldwide</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Open Positions Section */}
        <section id="openings" className="py-20 bg-gray-900/50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Open Positions
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Find your perfect role and help us build the future of energy storage.
              </p>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-10">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  placeholder="Search positions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:border-emerald-500 focus:outline-none"
                  data-testid="job-search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {departments.map((dept) => (
                  <button
                    key={dept.id}
                    onClick={() => setSelectedDepartment(dept.id)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      selectedDepartment === dept.id
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                    data-testid={`dept-filter-${dept.id}`}
                  >
                    {dept.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Job Listings */}
            <div className="space-y-4">
              {filteredPositions.length > 0 ? (
                filteredPositions.map((position) => (
                  <div
                    key={position.id}
                    className={`bg-gray-800/50 rounded-2xl p-6 border transition-all hover:border-emerald-500/30 ${
                      position.featured ? 'border-emerald-500/30' : 'border-white/5'
                    }`}
                    data-testid={`job-card-${position.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-white">{position.title}</h3>
                          {position.featured && (
                            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                              Featured
                            </span>
                          )}
                        </div>
                        <p className="text-gray-400 text-sm mb-4">{position.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-sm">
                          <span className="flex items-center gap-1 text-gray-500">
                            <MapPin className="w-4 h-4" />
                            {position.location}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Clock className="w-4 h-4" />
                            {position.type}
                          </span>
                          <span className="flex items-center gap-1 text-gray-500">
                            <Briefcase className="w-4 h-4" />
                            {position.experience}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-4">
                          {position.skills.map((skill, index) => (
                            <span
                              key={index}
                              className="px-3 py-1 bg-white/5 text-gray-400 text-xs rounded-full"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        onClick={() => handleApply(position)}
                        className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-6 py-3 rounded-xl transition-all whitespace-nowrap"
                        data-testid={`apply-btn-${position.id}`}
                      >
                        Apply Now
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-400">No positions found matching your criteria.</p>
                </div>
              )}
            </div>

            {/* General Application CTA */}
            <div className="mt-12 bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 rounded-2xl p-8 border border-emerald-500/20 text-center">
              <h3 className="text-xl font-bold text-white mb-3">Don't see the right role?</h3>
              <p className="text-gray-400 mb-6">
                We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
              </p>
              <a
                href={`mailto:${contactInfo.email}?subject=General Application - Careers at Gegadyne`}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium px-6 py-3 rounded-xl transition-all"
                data-testid="general-apply-btn"
              >
                <Mail className="w-5 h-5" />
                Send Your Resume
              </a>
            </div>
          </div>
        </section>

        {/* Connect Section */}
        <section className="py-20">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Stay Connected
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Follow us on social media to stay updated on new opportunities and company news.
            </p>
            <div className="flex justify-center gap-4">
              <a
                href={socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-emerald-500/20 text-white font-medium px-6 py-4 rounded-xl border border-white/10 hover:border-emerald-500/30 transition-all"
                data-testid="careers-linkedin"
              >
                <Linkedin className="w-5 h-5" />
                LinkedIn
                <ExternalLink className="w-4 h-4" />
              </a>
              <a
                href={socialLinks.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-white/5 hover:bg-emerald-500/20 text-white font-medium px-6 py-4 rounded-xl border border-white/10 hover:border-emerald-500/30 transition-all"
                data-testid="careers-twitter"
              >
                <Twitter className="w-5 h-5" />
                Twitter
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={companyInfo.logo} 
                alt={companyInfo.name}
                className="h-8 w-auto opacity-60"
              />
            </Link>
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} {companyInfo.name}. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Application Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-gray-900 rounded-2xl border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gray-900 px-6 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-white">Apply for Position</h3>
                <p className="text-emerald-400 text-sm">{selectedPosition?.title}</p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-2"
                data-testid="close-modal-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-emerald-400" />
                </div>
                
                <h4 className="text-xl font-bold text-white mb-3">
                  Apply via Email
                </h4>
                
                <p className="text-gray-400 mb-6">
                  Click the button below to open your email client with a pre-filled application template. 
                  Please attach your resume and complete the details.
                </p>

                {/* Position Details */}
                <div className="bg-gray-800/50 rounded-xl p-4 mb-6 text-left">
                  <h5 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Position Details</h5>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-300">
                      <Briefcase className="w-4 h-4 text-emerald-400" />
                      <span>{selectedPosition?.title}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <MapPin className="w-4 h-4 text-emerald-400" />
                      <span>{selectedPosition?.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <Clock className="w-4 h-4 text-emerald-400" />
                      <span>{selectedPosition?.experience} experience</span>
                    </div>
                  </div>
                </div>

                <a
                  href={generateMailtoLink()}
                  className="w-full flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white px-6 py-4 rounded-xl font-semibold transition-all"
                  data-testid="mailto-apply-btn"
                >
                  <Mail className="w-5 h-5" />
                  Open Email Application
                  <ArrowRight className="w-5 h-5" />
                </a>

                <p className="text-gray-500 text-xs mt-4">
                  Your default email client will open. Don't forget to attach your resume!
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CareersPage;
